import {
  BitkitIconComponent,
  IconAbortCircleFilled,
  IconCheckCircle,
  IconCircle,
  IconCrossCircleFilled,
  IconHourglass,
  IconPull,
  IconPush,
  IconSpinnerPurpleDouble,
  IconTag,
} from '@bitrise/bitkit-v2';
import { BoxProps } from '@chakra-ui/react/box';
import { TextProps } from '@chakra-ui/react/text';
import isNil from 'lodash/isNil';

import { PublicApiApp } from '@/models/App';
import { LogFormat } from '@/models/LogFormat';
import { Runnable } from '@/models/Runnable';
import { TriggerParams } from '@/models/TriggerParams';

import ArtifactTypeFilter from './ArtifactTypeFilter';
import BuildStatus from './BuildStatus';
import { BuildToolInvocation } from './BuildToolInvocation';

export type InstallableArtifact = {
  artifactType: ArtifactTypeFilter.APK | ArtifactTypeFilter.IPA;
  id: string;
  title: string;
  versionCode?: string;
  versionName?: string;
};

export type AIRunType = 'build-fixer' | 'code-reviewer' | 'build-fixer-multiturn' | 'ci-config-expert';

export type CommonBuild<StartedAtKey extends 'startedAt' | 'startedOnWorkerAt'> = Runnable<
  BuildStatus,
  StartedAtKey
> & {
  artifacts?: InstallableArtifact[];
  branch: string;
  buildNumber: number;
  commitHash?: string;
  commitMessage?: string;
  commitViewUrl?: string;
  isOnHold: boolean;
  isProcessed: boolean;
  isPipeline?: boolean;
  pipelineWorkflowId?: string | null;
  pullRequestId?: number;
  pullRequestTargetBranch?: string;
  repository?: PublicApiApp;
  rebuildable: boolean;
  slug: string;
  tag?: string;
  triggerParams?: TriggerParams;
  originalBuildParams?: TriggerParams;
  triggeredWorkflow: string | null;
  utility?: boolean;
  buildToolInvocations?: BuildToolInvocation[];
  agentRunSlug?: string;
  agentRunType?: AIRunType;
  aiRunType?: AIRunType;
  failureSummaryConfigured?: boolean | null;
  denTags?: string[];
  stackRollbackVersion?: string;
  configDatastoreUpdatedAt?: string | null;
};

export type Build = CommonBuild<'startedAt'>;

export type PublicApiBuild = CommonBuild<'startedOnWorkerAt'> & {
  commitViewUrl?: string;
  logFormat?: LogFormat;
  originalBuildParams: TriggerParams;
  pullRequestViewUrl?: string;
  statusText?: string;
  pipelineWorkflowId: string | null;
  triggeredWorkflow: string;
};

export type RemoteAccessData = {
  screenshare?: {
    address: string;
    password: string;
    username: string;
  };
  ssh: {
    command: string;
    host: string;
    port: string;
    user: string;
    password: string;
  };
};

export const isPullRequest = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  !!build?.pullRequestTargetBranch || !!build?.originalBuildParams?.branchDest;

export const sourceBranch = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string | undefined =>
  isPullRequest(build) ? build?.branch : undefined;

export const targetBranch = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string | undefined =>
  build.pullRequestTargetBranch || build.originalBuildParams?.branchDest || build.branch;

export const isBuildStarting = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build.status === BuildStatus.Running && isNil(build.startedOnWorkerAt) && isNil(build.startedAt);

export const getBuildStatusIconName = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): BitkitIconComponent => {
  switch (build.status) {
    case BuildStatus.Running:
      return build.isOnHold ? IconHourglass : IconSpinnerPurpleDouble;
    case BuildStatus.Success:
      return IconCheckCircle;
    case BuildStatus.Error:
      return IconCrossCircleFilled;
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return IconAbortCircleFilled;
    default:
      return IconCircle;
  }
};

export const getBuildStatusText = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): string => {
  if (build.isOnHold) {
    return 'On hold';
  }
  switch (build.status) {
    case BuildStatus.Running: {
      return isBuildStarting(build) ? 'Starting' : 'Running';
    }
    case BuildStatus.Success:
      return 'Success';
    case BuildStatus.Error:
      return 'Failed';
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return 'Aborted';
    default:
      throw new Error(`Unsupported build status: ${build.status}`);
  }
};

export const getTriggeredByText = (build: PublicApiBuild): string | undefined => {
  if (!build.triggeredBy) {
    return undefined;
  }

  if (build.triggeredBy === 'schedule') {
    return 'Schedule';
  }
  if (build.triggeredBy === 'webhook') {
    return 'Webhook';
  }
  if (build.triggeredBy === 'curl') {
    return 'cURL';
  }

  const manualPrefix = 'manual-';

  if (build.triggeredBy.startsWith(manualPrefix)) {
    const username = build.triggeredBy.replace(manualPrefix, '');
    return `Manual - @${username}`;
  }

  const webhookPrefix = 'webhook-';

  if (build.triggeredBy.startsWith(webhookPrefix)) {
    const providerAndUserName = build.triggeredBy.replace(webhookPrefix, '');
    return `Webhook - @${providerAndUserName}`;
  }

  return build.triggeredBy;
};

export enum TriggerTypes {
  PUSH = 'push',
  PULL_REQUEST = 'pull-request',
  TAG = 'tag',
}

const getTriggerType = ({ tag, pullRequestId }: { tag?: string; pullRequestId?: number | string }): TriggerTypes => {
  if (pullRequestId) {
    return TriggerTypes.PULL_REQUEST;
  }
  if (tag) {
    return TriggerTypes.TAG;
  }
  return TriggerTypes.PUSH;
};

export const getTriggerTypeIcon = ({
  tag,
  pullRequestId,
}: {
  tag?: string;
  pullRequestId?: number | string;
}): BitkitIconComponent => {
  const triggerType = getTriggerType({ tag, pullRequestId });
  switch (triggerType) {
    case TriggerTypes.PULL_REQUEST:
      return IconPull;
    case TriggerTypes.TAG:
      return IconTag;
    case TriggerTypes.PUSH:
    default:
      return IconPush;
  }
};

export const isBuildOnHold = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && build.status === BuildStatus.Running && build.isProcessed && build.isOnHold;

export const isBuildRunning = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): boolean =>
  build && build.status === BuildStatus.Running && build.isProcessed && !build.isOnHold;

export const isBuildStarted = (build: PublicApiBuild) => build && !!build.startedOnWorkerAt;

export const isBuildNotStarted = (build: PublicApiBuild) => !isBuildStarted(build);

export const isBuildAborted = (build: { status: BuildStatus }): boolean =>
  build && (build.status === BuildStatus.Aborted || build.status === BuildStatus.AbortedWithSuccess);

export const isBuildFinished = (build: { status: BuildStatus }): boolean =>
  build && build.status !== BuildStatus.Running;

export const isBuildNotFinished = (build: { status: BuildStatus }): boolean => build && !isBuildFinished(build);

export const getBuildStatusColors = (
  build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>,
): {
  color: BoxProps['color'];
  iconColor: BoxProps['color'];
  textColor: TextProps['color'];
  backgroundColor: BoxProps['color'];
  hoverBackgroundColor: BoxProps['color'];
} => {
  switch (build.status) {
    case BuildStatus.Running:
      if (build && isBuildOnHold(build)) {
        return {
          backgroundColor: 'status/neutral/bg',
          textColor: 'status/neutral/text',
          color: 'color/neutral/base',
          iconColor: 'icon/secondary',
          hoverBackgroundColor: 'status/neutral/bg-hover',
        };
      }

      if (build && isBuildStarting(build)) {
        return {
          backgroundColor: 'status/info/bg',
          textColor: 'status/info/text',
          color: 'color/blue/base',
          iconColor: 'status/info/icon',
          hoverBackgroundColor: 'status/info/bg-hover',
        };
      }

      return {
        backgroundColor: 'status/progress/bg',
        textColor: 'status/progress/text',
        color: 'color/purple/base',
        iconColor: 'icon/interactive',
        hoverBackgroundColor: 'status/progress/bg-hover',
      };
    case BuildStatus.Success:
      return {
        backgroundColor: 'status/success/bg',
        textColor: 'status/success/text',
        color: 'color/green/base',
        iconColor: 'icon/positive',
        hoverBackgroundColor: 'status/success/bg-hover',
      };
    case BuildStatus.Error:
      return {
        backgroundColor: 'status/critical/bg',
        textColor: 'status/critical/text',
        color: 'color/red/base',
        iconColor: 'icon/negative',
        hoverBackgroundColor: 'status/critical/bg-hover',
      };
    case BuildStatus.Aborted:
    case BuildStatus.AbortedWithSuccess:
      return {
        backgroundColor: 'status/warning/bg',
        textColor: 'status/warning/text',
        color: 'color/yellow/bold',
        iconColor: 'icon/warning',
        hoverBackgroundColor: 'status/warning/bg-hover',
      };
    default:
      return {
        backgroundColor: 'status/neutral/bg',
        textColor: 'status/neutral/text',
        color: 'color/neutral/base',
        iconColor: 'icon/secondary',
        hoverBackgroundColor: 'status/neutral/bg-hover',
      };
  }
};

const TenMinutesInMilliseconds = 6e5;
export const isRecentlyFinished = (build: PublicApiBuild): boolean => {
  if (!build.finishedAt) {
    return false;
  }

  const durationMilliseconds = Date.parse(build.finishedAt) - Date.now();
  return durationMilliseconds < TenMinutesInMilliseconds;
};

export const calculateDuration = (build: CommonBuild<'startedAt' | 'startedOnWorkerAt'>): number => {
  const startedAtString = build.startedAt ?? build.startedOnWorkerAt;
  if (!startedAtString) {
    return 0;
  }

  const startedAt = new Date(startedAtString);
  const finishedAt = build.finishedAt ? new Date(build.finishedAt) : new Date();

  return Math.round((finishedAt.getTime() - startedAt.getTime()) / 1000);
};

export const calculateProgressPercentage = (
  buildDuration: number,
  referenceBuildDuration: number,
  percentageCapIfRunning = 98,
): number => {
  if (referenceBuildDuration === 0) {
    return 0;
  }
  let percentage = (100 * buildDuration) / referenceBuildDuration;
  if (percentageCapIfRunning) {
    percentage = Math.min(percentageCapIfRunning, percentage);
  }

  return percentage;
};
